import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Branch, CreateBranch, UpdateBranch } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache key is keyed by companyId so switching company gives independent caches and
// invalidations don't bleed across DCC / TTOP / RCI.
const key = (companyId: string | undefined) => ['branches', companyId] as const;

export function useBranches(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Branch[]>(`/companies/${companyId}/branches?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useBranch(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['branches', companyId, id],
    queryFn: () => api<Branch>(`/companies/${companyId}/branches/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateBranch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateBranch, 'companyId'>) =>
      apiJson<typeof input, Branch>('POST', `/companies/${companyId}/branches`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateBranch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBranch }) =>
      apiJson<UpdateBranch, Branch>('PATCH', `/companies/${companyId}/branches/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteBranch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Branch>('DELETE', `/companies/${companyId}/branches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
